import {
  TIcons,
  Icon,
  statusColor,
  theme,
  CopyButton,
  palette,
} from '@yuno/design-system-web'
import {
  useVerifyPermission,
  useSnackbarContext,
} from '@yuno/dashboard-container-mfe'
import { PermissionsAvailable } from '@yuno/dashboard-authentication-mfe'
import { PermissionsTooltip } from '../common/permissions-tooltip'
import {
  Action,
  ButtonActive,
  ButtonArc,
  ButtonDelete,
  ButtonEdit,
  Category,
  CopyButtonStyled,
  Description,
  DivStyled,
  ImgWrapper,
  Pending,
  Title,
  TooltipStyled,
  TooltipStyledCopied,
  Wrapper,
} from './styles'
import { useTranslation } from 'react-i18next'

type TagColors = 'error' | 'info' | 'success' | 'warning' | 'default'

interface ConnectionCardSmallProps {
  category?: string
  providerId?: string
  img: string
  title: string
  fullWidth?: boolean
  name?: string
  handlerEdit?: () => void
  handlerArchive?: () => void
  archived?: boolean
  pending?: boolean
  tooltipTitle?: string
  icon?: TIcons
  iconColor?: TagColors
  integrationCode?: string
}

export const ConnectionCardSmall = ({
  category,
  img,
  title,
  fullWidth,
  handlerEdit,
  handlerArchive,
  archived,
  name,
  pending = false,
  tooltipTitle,
  icon,
  iconColor,
  integrationCode,
}: ConnectionCardSmallProps) => {
  const { allowed } = useVerifyPermission(
    PermissionsAvailable.MANAGE_CONNECTIONS,
  )
  const { onOpenSnackbar } = useSnackbarContext()
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const handleCopied = () => {
    onOpenSnackbar(t('copyClipboard'), 'success', 'CheckCircle')
  }

  return (
    <Wrapper
      fullWidth={fullWidth}
      prod={process.env.ENVIRONMENT !== ''}
      pending={pending}
    >
      <ImgWrapper width="80px" height="80px">
        <img width={'50px'} height={'50px'} src={img} alt={title} />
      </ImgWrapper>
      <div style={{ flex: 1 }}>
        <Title archived={archived}>{title}</Title>
        <Description archived={archived}>{name}</Description>
      </div>
      {!archived && (
        <TooltipStyledCopied
          placement={'bottom'}
          title={t('copyIdConnection')}
          color="default"
        >
          <DivStyled className="actions">
            <CopyButton
              sx={CopyButtonStyled}
              color={theme.palette.grey[5]}
              size={'base'}
              value={integrationCode}
              onCopied={handleCopied}
            />
          </DivStyled>
        </TooltipStyledCopied>
      )}
      {pending ? (
        <TooltipStyled
          color="custom-tooltip"
          placement={'bottom-end'}
          title={tooltipTitle}
          PopperProps={{
            style: { backgroundColor: palette.neutral.transparent },
          }}
          arrow
        >
          <Pending
            className="category"
            width={
              currentLanguage.toLocaleLowerCase() === 'en' ? '84px' : '94px'
            }
          >
            <Icon
              size={'15px'}
              name={icon}
              color={statusColor?.[iconColor]?.primary}
              weight={'bold'}
            />
            {t('pending')}
          </Pending>
        </TooltipStyled>
      ) : (
        <Category className="category">{category}</Category>
      )}
      {
        <Action className="actions">
          {!archived ? (
            <>
              <PermissionsTooltip allowed={allowed}>
                {/* @ts-ignore */}
                <ButtonArc
                  disabled={!allowed}
                  onClick={() => handlerArchive()}
                  variant="secondary"
                >
                  <Icon size="15px" name="Archive" />
                  Archive
                </ButtonArc>
              </PermissionsTooltip>
              <PermissionsTooltip allowed={allowed}>
                {/* @ts-ignore */}
                <ButtonEdit
                  disabled={!allowed}
                  onClick={() => handlerEdit()}
                  variant="primary"
                >
                  {t('edit')}
                </ButtonEdit>
              </PermissionsTooltip>
            </>
          ) : (
            <>
              <PermissionsTooltip allowed={allowed}>
                {/* @ts-ignore */}
                <ButtonActive
                  disabled={!allowed}
                  onClick={() => handlerArchive()}
                  variant="primary"
                >
                  <Icon size="15px" name="Lightning" />
                  Activate
                </ButtonActive>
              </PermissionsTooltip>
              <PermissionsTooltip allowed={allowed}>
                {/* @ts-ignore */}
                <ButtonDelete
                  disabled={!allowed}
                  onClick={() => handlerEdit()}
                  variant="primary"
                >
                  <Icon size="15px" name="Trash" />
                  {t('delete')}
                </ButtonDelete>
              </PermissionsTooltip>
            </>
          )}
        </Action>
      }
    </Wrapper>
  )
}
